import type { MutableRefObject } from 'react';
import { useMemo, useState } from 'react';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';

import { useStarterDashboardV2PageFcm } from 'src/app/pages/starterDashboardV2/pageContext';
import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import type { TaskShape } from '../../types';

import { useTasks } from './useTasks';

const DEFAULT_TASKS_DISPLAY_COUNT = 3;

type UseStepsToVictoryReturn = ModelHookResult<
	{
		tasks: TaskShape[];
		stats: {
			completed: number;
			count: number;
		};
		showDownloadFlyerTask: boolean;
		showTaskListToggle: boolean;
		toggleTaskListLabel: TranslationKey;
		containerRef: MutableRefObject<null>;
		viewMoreRef?: MutableRefObject<null>;
		getListItemRef: (itemIndex: number) => MutableRefObject<null> | undefined;
	},
	{
		toggleTaskList: () => void;
	}
>;

export function useStepsToVictory(): UseStepsToVictoryReturn {
	const { tasks: allTasks, stats } = useTasks();
	const { containerRef, loadMoreRef, getListItemRef, recordListEvent } = useLazyLoadedListFocus();
	const [taskListIsExpanded, setTaskListIsExpanded] = useState(false);

	const showTaskListToggle = allTasks.length > DEFAULT_TASKS_DISPLAY_COUNT;

	const toggleTaskListLabel = taskListIsExpanded
		? translationKey('corgi.petition.starter_dashboard.steps_to_victory.view_less_label')
		: translationKey('corgi.petition.starter_dashboard.steps_to_victory.view_more_label');

	const trimmedTasks = useMemo(() => allTasks.slice(0, DEFAULT_TASKS_DISPLAY_COUNT), [allTasks]);
	const displayedTasks = taskListIsExpanded ? allTasks : trimmedTasks;

	const toggleTaskList = () => {
		if (!taskListIsExpanded) {
			recordListEvent({ type: 'loadMore', itemsCount: displayedTasks.length, triggerFocusFallback: true });
		}
		setTaskListIsExpanded(!taskListIsExpanded);
	};

	const viewMoreRef = !taskListIsExpanded ? loadMoreRef : undefined;

	const fcms = useStarterDashboardV2PageFcm();
	const showDownloadFlyerTask = fcms.multipleQrCodeFlyersEnabled;

	return {
		data: {
			tasks: displayedTasks,
			stats,
			showTaskListToggle,
			showDownloadFlyerTask,
			toggleTaskListLabel,
			containerRef,
			viewMoreRef,
			getListItemRef,
		},
		actions: { toggleTaskList },
	};
}
